import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, Flame, RefreshCw,
  Linkedin, Mail, MessageSquare, Calendar, PlayCircle,
} from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

// 30-day sprint page — hard-gated for the CFO-appointment engine. Shows the
// four KPIs (LI accept, email open, email reply, meetings) vs baseline vs
// target so the user can eyeball daily whether we're trending toward the
// 30-day goal without waiting until day 30.
export default function Sprint() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/sprint/summary`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading)
    return (
      <Layout title="30-Day Sprint" subtitle="Loading…">
        <div className="text-sm text-white/50">Loading sprint metrics…</div>
      </Layout>
    );
  if (err)
    return (
      <Layout title="30-Day Sprint" subtitle="Error">
        <div className="text-red-400">Error: {err}</div>
      </Layout>
    );

  const {
    sprint, current, baseline, targets, delta_vs_baseline,
    pct_to_target, counts, trend, triggers, video_analytics,
  } = data;

  return (
    <Layout
      title="30-Day Sprint"
      subtitle={`Day ${sprint.day} of ${sprint.target_days} · ${sprint.days_remaining} days remaining`}
      actions={
        <Button
          data-testid="sprint-refresh-btn"
          variant="outline"
          size="sm"
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      }
    >
      <div className="space-y-8">
        {/* Sprint clock */}
        <SprintClock sprint={sprint} />

        {/* Four KPI cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            testId="kpi-linkedin-accept"
            icon={<Linkedin className="h-4 w-4" />}
            label="LinkedIn accept rate"
            current={current.linkedin_accept_rate}
            baseline={baseline.linkedin_accept_rate}
            target={targets.linkedin_accept_rate}
            pct_to_target={pct_to_target.linkedin_accept_rate}
            formatter={fmtPct}
            subtext={`${counts.linkedin_invites_accepted}/${counts.linkedin_invites_sent} invites`}
          />
          <KpiCard
            testId="kpi-cold-email-open"
            icon={<Mail className="h-4 w-4" />}
            label="Cold email open rate"
            current={current.cold_email_open_rate}
            baseline={baseline.cold_email_open_rate}
            target={targets.cold_email_open_rate}
            pct_to_target={pct_to_target.cold_email_open_rate}
            formatter={fmtPct}
            subtext={`${counts.cold_emails_uniquely_opened}/${counts.cold_emails_sent} sends`}
          />
          <KpiCard
            testId="kpi-cold-email-reply"
            icon={<MessageSquare className="h-4 w-4" />}
            label="Cold email reply rate"
            current={current.cold_email_reply_rate}
            baseline={baseline.cold_email_reply_rate}
            target={targets.cold_email_reply_rate}
            pct_to_target={pct_to_target.cold_email_reply_rate}
            formatter={fmtPct}
            subtext={`${counts.cold_email_replies} replies`}
          />
          <KpiCard
            testId="kpi-meetings"
            icon={<Calendar className="h-4 w-4" />}
            label="Meetings booked"
            current={current.meetings_booked}
            baseline={baseline.meetings_booked}
            target={targets.meetings_booked}
            pct_to_target={pct_to_target.meetings_booked}
            formatter={(v) => v}
            subtext="Sprint-to-date"
            isMeeting
          />
        </div>

        {/* Trend chart */}
        <TrendPanel trend={trend} />

        {/* Video CTA views — how many prospects actually clicked the video */}
        <VideoAnalyticsPanel video={video_analytics || {}} trend={trend} />

        {/* Trigger coverage */}
        <TriggerPanel triggers={triggers} />

        {/* Channel health — why KPIs might be stuck */}
        <ChannelHealthPanel health={data.channel_health || {}} />

        {/* Verdict */}
        <VerdictPanel pct_to_target={pct_to_target} sprint={sprint} current={current} targets={targets} />
      </div>
    </Layout>
  );
}

// ─────────── SUB-COMPONENTS ───────────

function SprintClock({ sprint }) {
  const pct = Math.min(100, (sprint.days_elapsed / sprint.target_days) * 100);
  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="sprint-clock">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">
            Day {sprint.day} of {sprint.target_days}
          </div>
          <div className="text-3xl font-light mt-1 text-foreground">
            {sprint.days_remaining} days remaining
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          Started {new Date(sprint.start_iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </div>
      <div className="h-1.5 bg-muted rounded overflow-hidden">
        <div className="h-full bg-amber-500 rounded" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function KpiCard({ testId, icon, label, current, baseline, target, pct_to_target, formatter, subtext, isMeeting }) {
  const delta = current - baseline;
  const trending = delta > 0.0001 ? "up" : delta < -0.0001 ? "down" : "flat";
  const pctOfTarget = Math.max(-25, Math.min(150, pct_to_target * 100));
  const status =
    pct_to_target >= 1 ? "on-target"
    : pct_to_target >= 0.5 ? "trending"
    : pct_to_target > 0 ? "slow"
    : "flat";
  const statusColor = {
    "on-target": "text-emerald-600",
    "trending":  "text-amber-600",
    "slow":      "text-orange-600",
    "flat":      "text-muted-foreground",
  }[status];
  const barColor = {
    "on-target": "bg-emerald-500",
    "trending":  "bg-amber-500",
    "slow":      "bg-orange-500",
    "flat":      "bg-muted-foreground/30",
  }[status];

  return (
    <div className="border border-border rounded-lg p-5 bg-card" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </div>
        {trending === "up" && <TrendingUp className="h-4 w-4 text-emerald-600" />}
        {trending === "down" && <TrendingDown className="h-4 w-4 text-red-500" />}
        {trending === "flat" && <Minus className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="text-4xl font-light tracking-tight mb-1 text-foreground" data-testid={`${testId}-value`}>
        {formatter(current)}
      </div>
      <div className="text-xs text-muted-foreground mb-4">{subtext}</div>
      <div className="space-y-1.5">
        <div className="flex justify-between text-[11px] text-muted-foreground">
          <span>Baseline {formatter(baseline)}</span>
          <span>Target {formatter(target)}</span>
        </div>
        <div className="h-1.5 bg-muted rounded relative overflow-hidden">
          <div className={`h-full ${barColor} rounded transition-all`} style={{ width: `${Math.max(0, pctOfTarget)}%` }} />
        </div>
        <div className={`text-[11px] font-semibold ${statusColor}`} data-testid={`${testId}-status`}>
          {status === "on-target" && "✓ At or above target"}
          {status === "trending"  && `${Math.round(pctOfTarget)}% to target — trending well`}
          {status === "slow"      && `${Math.round(pctOfTarget)}% to target — needs lift`}
          {status === "flat"      && "No movement vs baseline"}
        </div>
      </div>
    </div>
  );
}

function TrendPanel({ trend }) {
  const days = trend.days || [];
  if (days.length === 0) return null;
  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="sprint-trend">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4">Daily Trend</div>
      <TrendRow label="LinkedIn invites"   values={trend.linkedin_invites}   days={days} color="#0ea5e9" />
      <TrendRow label="Invite accepts"     values={trend.linkedin_accepts}   days={days} color="#059669" />
      <TrendRow label="InMails sent"       values={trend.linkedin_inmails}   days={days} color="#6366f1" />
      <TrendRow label="Cold emails sent"   values={trend.cold_emails_sent}   days={days} color="#f59e0b" />
      <TrendRow label="Cold email opens"   values={trend.cold_email_opens}   days={days} color="#d97706" />
      <TrendRow label="Cold email replies" values={trend.cold_email_replies} days={days} color="#db2777" />
      <TrendRow label="Video CTA views"    values={trend.video_views || []}  days={days} color="#a67c00" isLast />
    </div>
  );
}

function TrendRow({ label, values, days, color, isLast }) {
  const max = Math.max(1, ...values);
  return (
    <div className={`flex items-center gap-4 ${!isLast ? "border-b border-border pb-3 mb-3" : ""}`}>
      <div className="w-40 text-xs text-muted-foreground shrink-0">{label}</div>
      <div className="flex-1 flex items-end gap-1 h-10">
        {values.map((v, i) => (
          <div
            key={i}
            className="flex-1 rounded-t opacity-80 hover:opacity-100 transition-opacity"
            title={`${days[i]}: ${v}`}
            style={{
              height: `${(v / max) * 100}%`,
              minHeight: v > 0 ? 3 : 1,
              background: v > 0 ? color : "rgba(0,0,0,0.08)",
            }}
          />
        ))}
      </div>
      <div className="w-16 text-right text-sm font-mono text-foreground">
        {values.reduce((a, b) => a + b, 0)}
      </div>
    </div>
  );
}

function TriggerPanel({ triggers }) {
  const target = triggers.leads_target || 0;
  const scanned = triggers.leads_scanned || 0;
  const withEvent = triggers.leads_with_event || 0;
  const pct = target ? Math.min(100, (scanned / target) * 100) : 0;
  const scanComplete = target > 0 && scanned >= target;
  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="sprint-triggers">
      <div className="flex items-center gap-3 mb-4">
        <Flame className="h-6 w-6 text-orange-500" strokeWidth={1.5} />
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Trigger coverage</div>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mb-4">
        <div>
          <div className="text-3xl font-light text-foreground" data-testid="triggers-total-stored">{triggers.total_stored}</div>
          <div className="text-xs text-muted-foreground mt-1">News events stored</div>
        </div>
        <div>
          <div className="text-3xl font-light text-orange-600" data-testid="triggers-hot-leads">{triggers.hot_leads}</div>
          <div className="text-xs text-muted-foreground mt-1">Hot leads flagged</div>
        </div>
        <div>
          <div className="text-3xl font-light text-emerald-600" data-testid="triggers-with-event">{withEvent}</div>
          <div className="text-xs text-muted-foreground mt-1">Leads with trigger event</div>
        </div>
      </div>
      {target > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Scan progress · {scanned.toLocaleString()} of {target.toLocaleString()} AU/NZ leads</span>
            <span className={scanComplete ? "text-emerald-600 font-semibold" : "font-mono"}>{pct.toFixed(1)}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded overflow-hidden">
            <div
              className={`h-full rounded transition-all ${scanComplete ? "bg-emerald-500" : "bg-orange-500"}`}
              style={{ width: `${pct}%` }}
              data-testid="triggers-scan-progress"
            />
          </div>
          <div className="text-[11px] text-muted-foreground mt-2">
            {scanComplete
              ? "✓ Full DB scan complete — every AU/NZ CFO checked for a trigger event."
              : `${(target - scanned).toLocaleString()} leads still to scan · Perplexity backfill running in the background.`}
          </div>
        </div>
      )}
      {triggers.last_scan_at && (
        <div className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
          Last scan {new Date(triggers.last_scan_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function VideoAnalyticsPanel({ video, trend }) {
  const total = video.views_total || 0;
  const unique = video.views_unique_leads || 0;
  const v24 = video.views_24h || 0;
  const bySrc = video.views_by_source || {};
  const sources = Object.entries(bySrc).sort((a, b) => b[1] - a[1]);
  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="sprint-video-analytics">
      <div className="flex items-center gap-3 mb-5">
        <PlayCircle className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Video CTA views</div>
        <div className="text-[10px] text-muted-foreground/70 ml-auto">Anyone who clicked your outreach video link</div>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mb-4">
        <div>
          <div className="text-4xl font-light text-foreground" data-testid="video-views-total">{total}</div>
          <div className="text-xs text-muted-foreground mt-1">Total clicks (all-time)</div>
        </div>
        <div>
          <div className="text-4xl font-light text-emerald-600" data-testid="video-views-unique">{unique}</div>
          <div className="text-xs text-muted-foreground mt-1">Unique leads who watched</div>
        </div>
        <div>
          <div className="text-4xl font-light text-amber-600" data-testid="video-views-24h">{v24}</div>
          <div className="text-xs text-muted-foreground mt-1">Clicks in last 24h</div>
        </div>
      </div>
      {sources.length > 0 && (
        <div className="pt-4 border-t border-border">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">By channel</div>
          <div className="flex flex-wrap gap-2">
            {sources.map(([src, n]) => (
              <span
                key={src}
                data-testid={`video-source-${src}`}
                className="text-xs font-mono px-2.5 py-1 border border-border rounded bg-muted/30"
              >
                {SOURCE_LABEL[src] || src}: <span className="text-amber-600 font-semibold">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {total === 0 && (
        <div className="text-xs text-muted-foreground mt-2">
          No clicks yet — this is the raw signal you'll watch to know if the video hook is working.
          As soon as a prospect opens the link, they show up here in real time.
        </div>
      )}
    </div>
  );
}

const SOURCE_LABEL = {
  email: "Cold email",
  li_dm: "LinkedIn DM",
  li_inmail: "LinkedIn InMail",
  linkedin: "LinkedIn",
  unknown: "Other",
};

function ChannelHealthPanel({ health }) {
  const items = [];
  if (health.outreach_video_url) {
    items.push({
      label: "Outreach video",
      value: `LIVE · ${health.video_wired_leads || 0} leads`,
      tone: "ok",
      hint: `${health.outreach_video_title || "60-sec case-study video"} — wired into cold email step 3 + LinkedIn DM step 4.`,
    });
  }
  const credits = health.sales_navigator_credits;
  if (credits !== null && credits !== undefined) {
    items.push({
      label: "Sales Nav InMail credits",
      value: credits,
      tone: credits === 0 ? "warn" : credits < 10 ? "amber" : "ok",
      hint: credits === 0
        ? "Exhausted — refills on your Sales Nav billing anniversary. InMail sends paused until then."
        : credits < 10 ? "Running low."
        : "Healthy.",
    });
  }
  if (health.linkedin_invite_eligible_leads !== undefined) {
    const n = health.linkedin_invite_eligible_leads;
    const onPersona = health.linkedin_invite_eligible_on_persona;
    const parked = health.linkedin_off_persona_parked || 0;
    const showPersona = typeof onPersona === "number";
    items.push({
      label: "Invite-eligible AU/NZ leads",
      value: showPersona ? `${onPersona} / ${n}` : n,
      tone: (showPersona ? onPersona : n) < 50 ? "warn"
            : (showPersona ? onPersona : n) < 200 ? "amber" : "ok",
      hint: showPersona
        ? `${onPersona} on-persona (CFO / Finance / COO / CEO / MD / Owner / Procurement) out of ${n} total in queue. ${parked} off-persona leads parked. Only on-persona leads receive invites.`
        : (n < 50 ? "Queue running low — Apollo saved-search may need a bump."
            : n < 200 ? "OK but plan for restock in a week." : "Healthy queue."),
    });
  }
  if (health.linkedin_inmails_disabled) {
    items.push({
      label: "InMail sending",
      value: "DISABLED",
      tone: "warn",
      hint: `Reason: ${health.linkedin_inmails_disabled_reason || "unknown"}${
        health.linkedin_inmails_disabled_at
          ? ` · since ${new Date(health.linkedin_inmails_disabled_at).toLocaleDateString()}`
          : ""
      }`,
    });
  }
  if (health.linkedin_provider_throttled_until) {
    items.push({
      label: "Unipile throttle",
      value: "ACTIVE",
      tone: "warn",
      hint: `Until ${new Date(health.linkedin_provider_throttled_until).toLocaleString()}`,
    });
  }
  if (items.length === 0) return null;
  const toneClass = {
    ok:    "text-emerald-600",
    amber: "text-amber-600",
    warn:  "text-red-600",
  };
  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="sprint-channel-health">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4">Channel Health</div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="flex items-baseline justify-between gap-4 border-b border-border pb-3 last:border-b-0 last:pb-0">
            <div className="min-w-0">
              <div className="text-sm text-foreground">{it.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{it.hint}</div>
            </div>
            <div className={`text-lg font-mono ${toneClass[it.tone]} shrink-0`}>{it.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function VerdictPanel({ pct_to_target, sprint, current, targets }) {
  const pcts = Object.values(pct_to_target);
  const onTargetCount = pcts.filter((p) => p >= 1).length;
  const trendingCount = pcts.filter((p) => p >= 0.5 && p < 1).length;
  const flatCount = pcts.filter((p) => p <= 0).length;

  let verdict, verdictColor, message;
  if (onTargetCount >= 3) {
    verdict = "ON TRACK";
    verdictColor = "text-emerald-600";
    message = `${onTargetCount}/4 KPIs at or above target with ${sprint.days_remaining} days left. The engine is working.`;
  } else if (onTargetCount + trendingCount >= 2) {
    verdict = "TRENDING WELL";
    verdictColor = "text-amber-600";
    message = `${onTargetCount} KPIs on target, ${trendingCount} trending well. Continue the sprint.`;
  } else if (flatCount >= 3 && sprint.days_elapsed >= 15) {
    verdict = "REVIEW";
    verdictColor = "text-orange-600";
    message = `${flatCount}/4 KPIs flat past halfway point. Diagnose what's blocking before day 30.`;
  } else {
    verdict = "EARLY DAYS";
    verdictColor = "text-muted-foreground";
    message = `Give the trigger-based hooks 5-7 days to work through the cadence before drawing conclusions.`;
  }

  return (
    <div className="border border-border rounded-lg p-6 bg-card" data-testid="sprint-verdict">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Verdict</div>
      <div className={`text-3xl font-light tracking-tight ${verdictColor} mb-2`}>{verdict}</div>
      <div className="text-sm text-foreground/80 leading-relaxed">{message}</div>
    </div>
  );
}

function fmtPct(v) {
  return `${(v * 100).toFixed(1)}%`;
}
